import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Buy box of a bartender kit (blocks/kit-buy-box.liquid).
 * Keeps the total on the button in step with the ticked add-ons, counts all ticked decks as the
 * cheaper bundle, shows the hint about the next kit, writes the dispatch date in Kyiv time, names
 * the colour square under the mouse in the label above the squares and adds the kit with its
 * add-ons in one cart request, telling the cart drawer and the cart icon the same way the product
 * form does (assets/product-form.js), then opens the cart drawer. Without the script the form adds
 * the kit alone.
 */
class KitBuyBox extends HTMLElement {
  /** @type {Record<string, any>} */
  #config = {};

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLElement[]} */
  #totals = [];

  /** @type {HTMLButtonElement[]} */
  #buttons = [];

  /** @type {HTMLElement | null} */
  #sticky = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #buttonScrolledPast = false;
  #buttonOutOfView = false;
  #footerVisible = false;

  connectedCallback() {
    try {
      this.#config = JSON.parse(this.querySelector('script[data-kit-config]')?.textContent || '{}');
    } catch {
      this.#config = {};
    }

    this.#form = this.querySelector('form');
    this.#totals = [...this.querySelectorAll('[data-total]')].filter((element) => element instanceof HTMLElement);
    this.#buttons = [...this.querySelectorAll('[data-submit], [data-sticky-submit]')].filter(
      (element) => element instanceof HTMLButtonElement
    );

    this.addEventListener('change', this.#onChange);
    this.#form?.addEventListener('submit', this.#onSubmit);

    this.#showDispatch();
    this.#setUpColours();
    this.#update();
    this.#setUpSticky();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#form?.removeEventListener('submit', this.#onSubmit);
    this.#observer?.disconnect();
    this.#sticky?.remove();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.matches('[data-deck-bundle]')) {
      for (const deck of this.#decks()) deck.checked = input.checked;
    } else if (input.matches('[data-deck]')) {
      const bundle = this.#bundle();
      if (bundle) bundle.checked = this.#decks().every((deck) => deck.checked);
    } else if (input.matches('[data-addon-option]')) {
      const row = input.closest('[data-kit-addon]');
      const check = row?.querySelector('[data-addon-check]');
      if (check instanceof HTMLInputElement) check.checked = true;
      this.#showOption(row, input);
    }

    this.#update();
  };

  /** @returns {HTMLInputElement[]} */
  #decks() {
    return [...this.querySelectorAll('input[data-deck]')].filter((input) => input instanceof HTMLInputElement);
  }

  /** @returns {HTMLInputElement | null} */
  #bundle() {
    const bundle = this.querySelector('input[data-deck-bundle]');
    return bundle instanceof HTMLInputElement ? bundle : null;
  }

  /** All decks are ticked and there is a bundle to put in the cart instead */
  #decksAsBundle() {
    const decks = this.#decks();
    return Boolean(this.#bundle() && decks.length && decks.every((deck) => deck.checked));
  }

  /**
   * The input that holds the variant of a ticked add-on row: the chip when the row has a choice,
   * the checkbox otherwise. Null when the row isn't ticked.
   * @param {Element} row
   * @returns {HTMLInputElement | null}
   */
  #rowChoice(row) {
    const check = row.querySelector('[data-addon-check]');
    if (!(check instanceof HTMLInputElement) || !check.checked) return null;

    const option = row.querySelector('[data-addon-option]:checked');
    return option instanceof HTMLInputElement ? option : check;
  }

  /**
   * @param {Element | null} row
   * @param {HTMLInputElement} option
   */
  #showOption(row, option) {
    const image = row?.querySelector('.kit-addon__media img');
    if (image instanceof HTMLImageElement && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }

    const price = row?.querySelector('[data-addon-price]');
    if (price && option.dataset.priceLabel) price.textContent = option.dataset.priceLabel;
  }

  #update() {
    const kitPrice = Number(this.dataset.price) || 0;
    let total = kitPrice;

    const asBundle = this.#decksAsBundle();
    if (asBundle) {
      total += Number(this.#bundle()?.dataset.price) || 0;
    } else {
      for (const deck of this.#decks()) {
        if (deck.checked) total += Number(deck.dataset.price) || 0;
      }
    }

    const bundleNote = this.querySelector('[data-bundle-note]');
    if (bundleNote instanceof HTMLElement) bundleNote.hidden = !asBundle;

    let ownRows = 0;
    let ownTicked = 0;
    let ownTotal = 0;

    for (const row of this.querySelectorAll('[data-kit-addon]')) {
      const choice = this.#rowChoice(row);
      const price = choice ? Number(choice.dataset.price) || 0 : 0;
      total += price;

      if (row instanceof HTMLElement && row.dataset.kind === 'kit') {
        ownRows += 1;
        if (choice) {
          ownTicked += 1;
          ownTotal += price;
        }
      }
    }

    // The next kit already has the kit's own add-ons: show how little it costs on top of them
    const upgrade = this.querySelector('[data-upgrade]');
    if (upgrade instanceof HTMLElement) {
      const difference = (Number(upgrade.dataset.price) || 0) - kitPrice - ownTotal;
      const show = ownRows > 0 && ownTicked === ownRows && difference > 0;
      upgrade.hidden = !show;

      const label = upgrade.querySelector('[data-upgrade-difference]');
      if (show && label) label.textContent = this.#money(difference);
    }

    for (const element of this.#totals) element.textContent = this.#money(total);
  }

  /** @param {number} cents */
  #money(cents) {
    const { moneyFormat, currency } = this.#config;
    if (!moneyFormat) return String(Math.round(cents / 100));
    return formatMoney(cents, moneyFormat, currency);
  }

  /** @returns {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} */
  #items() {
    if (!this.#form) return [];

    const formData = new FormData(this.#form);
    /** @type {Record<string, string>} */
    const properties = {};
    for (const [key, value] of formData) {
      const name = /^properties\[(.+)\]$/.exec(key)?.[1];
      if (name && typeof value === 'string' && value) properties[name] = value;
    }

    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }} */
    const kit = { id: Number(formData.get('id')), quantity: 1 };
    if (Object.keys(properties).length) kit.properties = properties;

    /** @type {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} */
    const items = [];

    if (this.#decksAsBundle()) {
      items.push({ id: Number(this.#bundle()?.value), quantity: 1 });
    } else {
      for (const deck of this.#decks()) {
        if (deck.checked) items.push({ id: Number(deck.value), quantity: 1 });
      }
    }

    for (const row of this.querySelectorAll('[data-kit-addon]')) {
      const choice = this.#rowChoice(row);
      if (choice) items.push({ id: Number(choice.value), quantity: 1 });
    }

    // The cart lists the last added line first, so the kit goes last to head the cart
    items.push(kit);

    return items.filter((item) => item.id);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = this.#form;
    if (!form) return;

    event.preventDefault();
    if (this.#buttons.some((button) => button.getAttribute('aria-busy') === 'true')) return;

    const items = this.#items();
    if (!items.length) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    this.#showError('');
    for (const button of this.#buttons) button.setAttribute('aria-busy', 'true');

    const deferred = CartLinesUpdateEvent.createPromise();
    let added = false;
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // sections_url: the drawer comes back in the page's language; without it, Ukrainian on /ru
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      const cart = await this.#fetchCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kit-buy-box',
          sourceId: form.id,
          itemCount: items.length,
          didError: Boolean(data.status),
          sections: data.status ? undefined : data.sections,
        },
      });

      if (data.status) {
        form.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        this.#showError(data.description || data.message || '');
      } else {
        this.#showAdded();
        added = true;
      }
    } catch (error) {
      deferred.reject(error);
      // Something went wrong with the request: fall back to the ordinary form, which adds the kit
      form.submit();
    } finally {
      for (const button of this.#buttons) button.removeAttribute('aria-busy');
    }

    if (added) {
      // After the cart drawer has taken the new contents from the response
      await deferred.promise;
      openCartDrawer();
    }
  };

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /** The button says the kit is in the cart: the only sign of it when the cart is a page, not a drawer */
  #showAdded() {
    const status = this.querySelector('[data-status]');
    const added = this.querySelector('.kit-buy-box__added')?.textContent?.trim() ?? '';
    if (status) status.textContent = added;

    for (const button of this.#buttons) {
      button.dataset.added = '';
      clearTimeout(Number(button.dataset.addedTimer));
      button.dataset.addedTimer = String(
        setTimeout(() => {
          delete button.dataset.added;
          if (status) status.textContent = '';
        }, 2000)
      );
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('.kit-buy-box__error');
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = !message;
    if (message && this.#sticky?.hasAttribute('data-visible')) {
      error.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  /**
   * "Order before 17:00 — we ship today, you get it tomorrow, 2 October". We ship on working days
   * (Monday to Friday) and the parcel travels `deliveryDays` days. The server renders a general
   * sentence for visitors without the script.
   */
  #showDispatch() {
    const target = this.querySelector('[data-dispatch]');
    const { dispatchToday, dispatchLater, days, weekdays, locale } = this.#config;
    if (!target || !dispatchToday || !dispatchLater || !Array.isArray(days) || !Array.isArray(weekdays)) return;

    const cutoff = Number(this.#config.cutoffHour) || 17;
    const transit = Number(this.#config.deliveryDays) || 1;
    const now = kyivNow();
    if (!now) return;

    /** @param {Date} date */
    const isWorkday = (date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;

    const dispatch = new Date(now.date);
    const today = isWorkday(dispatch) && now.hour < cutoff;
    if (!today) {
      do dispatch.setUTCDate(dispatch.getUTCDate() + 1);
      while (!isWorkday(dispatch));
    }

    const arrival = new Date(dispatch);
    arrival.setUTCDate(arrival.getUTCDate() + transit);

    /** @param {Date} date */
    const when = (date) => {
      const offset = Math.round((date.getTime() - now.date.getTime()) / 86_400_000);
      return days[offset] ?? weekdays[date.getUTCDay()];
    };

    let arrivalDate = '';
    try {
      arrivalDate = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(arrival);
    } catch {
      return;
    }

    target.textContent = (today ? dispatchToday : dispatchLater)
      .replace('[hour]', String(cutoff))
      .replace('[dispatch]', when(dispatch))
      .replace('[arrival]', `${when(arrival)}, ${arrivalDate}`);
  }

  /**
   * The colour squares carry no text: the label above them names the colour and its price. On a
   * computer it shows the square under the mouse or in keyboard focus and goes back to this kit's
   * colour after. A tap on a phone simply opens the other colour, whose page names it.
   */
  #setUpColours() {
    const list = this.querySelector('[data-colours]');
    const name = this.querySelector('[data-colour-shown]');
    const price = this.querySelector('[data-colour-shown-price]');
    if (!(list instanceof HTMLElement) || !name || !price) return;

    const own = { name: name.textContent ?? '', price: price.textContent ?? '' };

    /** @param {EventTarget | null} target */
    const show = (target) => {
      const square = target instanceof Element ? target.closest('[data-colour-name]') : null;
      if (!(square instanceof HTMLElement)) return;
      name.textContent = square.dataset.colourName ?? own.name;
      price.textContent = square.dataset.colourPrice ?? own.price;
    };
    const reset = () => {
      name.textContent = own.name;
      price.textContent = own.price;
    };

    list.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'mouse') show(event.target);
    });
    list.addEventListener('pointerleave', reset);
    list.addEventListener('focusin', (event) => show(event.target));
    list.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !list.contains(event.relatedTarget)) reset();
    });
  }

  /**
   * A bar at the bottom of the screen with the same button: on a phone from the first screen, whenever
   * the main button is out of view (on a phone it sits two screens down, under the options); on a
   * computer once the main button has scrolled past
   */
  #setUpSticky() {
    const sticky = this.querySelector('[data-sticky]');
    const button = this.querySelector('[data-submit]');
    if (!(sticky instanceof HTMLElement) || !(button instanceof HTMLElement)) return;

    // Out of the product column, so that its layout can't hold the fixed bar in place
    if (!window.Shopify?.designMode && 'IntersectionObserver' in window) {
      document.body.append(sticky);
    } else {
      sticky.remove();
      return;
    }

    this.#sticky = sticky;
    sticky.querySelector('[data-sticky-submit]')?.addEventListener('click', () => this.#form?.requestSubmit());

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    const phone = window.matchMedia('(max-width: 749px)');

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === button) {
          this.#buttonScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.#buttonOutOfView = !entry.isIntersecting;
        } else {
          this.#footerVisible = entry.isIntersecting;
        }
      }
      const wanted = phone.matches ? this.#buttonOutOfView : this.#buttonScrolledPast;
      sticky.toggleAttribute('data-visible', wanted && !this.#footerVisible);
    });

    this.#observer.observe(button);
    if (footer) this.#observer.observe(footer);
  }
}

/**
 * Opens the cart drawer, so that the buyer sees the kit and its add-ons in the cart. The theme
 * setting that opens the drawer on every add stays off: it would open on each «+» of the
 * quick-order shelves too.
 */
function openCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (typeof drawer?.open === 'function') drawer.open();
}

/**
 * Today's date (at midnight UTC, for counting days) and the hour in Kyiv
 * @returns {{ date: Date, hour: number } | null}
 */
function kyivNow() {
  for (const timeZone of ['Europe/Kyiv', 'Europe/Kiev']) {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
          timeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        })
          .formatToParts(new Date())
          .map((part) => [part.type, part.value])
      );

      return {
        date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))),
        hour: Number(parts.hour),
      };
    } catch {
      // An old browser that doesn't know the new name of the time zone
    }
  }
  return null;
}

if (!customElements.get('kit-buy-box')) {
  customElements.define('kit-buy-box', KitBuyBox);
}
